import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../utils/api';
import { toLocalISO, formatDateShort } from '../utils/dateFormat';
import { useDepartment } from '../contexts/DepartmentContext';
import { useVatMode } from '../hooks/useVatMode';
import { useToast } from '../contexts/ToastContext';
import { usePageTitle } from '../contexts/PageHeaderContext';
import { friendlyErrorMessage } from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import { getBudgetHealth, BUDGET_HEALTH_COLORS } from '../constants/budget';
import { buildBudgetVariancePdf } from '../utils/budgetVariancePdf';
import SharePdfButton from '../components/SharePdfButton';
import BudgetTabsNav, { BUDGET_TABS_BAND, BUDGET_TABS_ROW } from '../components/budget/BudgetTabsNav';
import SpendAdjustmentMarker from '../components/SpendAdjustmentMarker';
import CompareViewBody from '../components/CompareViewBody';
import BudgetTableScroller from '../components/budget/BudgetTableScroller';
import BudgetComparePicker from '../components/budget/BudgetComparePicker';
import { useBudgetCompareSelection } from '../components/budget/budgetCompareSelection';
import { TOOLBAR_DISCLOSURE_TRIGGER } from '../components/ui/Toolbar';
import CollapsibleSection from '../components/CollapsibleSection';
import useBudgetInvalidation from '../hooks/useBudgetInvalidation';
import { PAGE_SIDE_GUTTER } from '../constants/pageLayout';

/**
 * Display percentages for a cost-code row (F7, 2026-08-15).
 *
 * A NEGATIVE net spend is now reachable — a manual spend adjustment can pull
 * `cost_to_date` below zero — and the raw arithmetic then OVER-CLAIMS:
 * "% Left" reads 120.0% and the health % goes negative, both of which read as
 * data errors. Display clamps only; the underlying figures are untouched and
 * the amber SpendAdjustmentMarker beside the Spent figure is what explains the
 * negative. Overspend is deliberately NOT capped (percentSpend > 100 is a real
 * warning); only the negative side is floored. Exported for the leaf test
 * (the BudgetCostCodes precedent — the page itself is never mounted).
 */
export function ccDisplayPercents(budgetAmount, costToDate) {
  const budget = Number(budgetAmount) || 0;
  const spend = Number(costToDate) || 0;
  const rawPercentSpend = budget > 0 ? (spend / budget) * 100 : (spend > 0 ? 100 : 0);
  return {
    percentSpend: Math.max(0, rawPercentSpend),
    percentLeft: budget > 0 ? Math.min(100, Math.max(0, 100 - rawPercentSpend)) : 0,
  };
}

const todayISO = () => toLocalISO(new Date());

// Compare drawer helpers (labels, formatters, impact maths, allowance grouping)
// live in `utils/compareDrawer.js`. The PDF Variance Analysis layout uses the
// same module so the on-screen drawer and the printed report stay in lockstep.

function BudgetOverview() {
  const { activeDepartment, isAdmin, isBuyer } = useDepartment();
  const { formatMoney } = useVatMode();
  const toast = useToast();
  const navigate = useNavigate();
  const departmentId = activeDepartment?.department_id;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [asOfDate, setAsOfDate] = useState(todayISO);
  const [summaryView, setSummaryView] = useState(() => {
    try { return localStorage.getItem('sfx_budget_overview_summary') === 'true'; } catch { return false; }
  });
  // Expansion of the Total Allowances roll-up (Computer + named slots).
  // Always collapsed by default on page load — keeps the table compact for
  // the common "glance at the numbers" use case. Session-only.
  const [allowancesExpanded, setAllowancesExpanded] = useState(false);
  // Expansion of the Crew Rates row → per-crew-member breakdown. Same
  // session-only / default-collapsed model as allowances.
  const [laboursExpanded, setLaboursExpanded] = useState(false);

  // Phase 10 / Task 17 — inline Compare drawer. Click "Compare" on a
  // non-current version column header → fetch the diff vs the current
  // version and pop the drawer as an absolutely-positioned panel above
  // the table (NOT a modal, so the underlying Gantt/table layout is
  // preserved). The "unchanged" section is collapsible inside the drawer.
  const [compareDrawerOpen, setCompareDrawerOpen] = useState(false);
  const [compareTargetVersionId, setCompareTargetVersionId] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [compareUnchangedExpanded, setCompareUnchangedExpanded] = useState(false);
  const [compareCostCodesExpanded, setCompareCostCodesExpanded] = useState(true);
  const [compareLabourExpanded, setCompareLabourExpanded] = useState(true);

  // PDF Variance Analysis anchor. Resolution order:
  //   1. If the user has flagged a version with is_baseline=1 AND that
  //      version is NOT the current one → use it as the anchor.
  //   2. If the user has flagged the CURRENT version as baseline →
  //      return null. Variance against self is always zero, so the
  //      Variance section is meaningless; the PDF should drop it
  //      entirely and just show the Cost Report.
  //   3. Else (no explicit flag anywhere) → fall back to the oldest
  //      non-current version by id — the original legacy rule.
  // The Compare drawer's interactive selection does NOT influence the
  // PDF — that surface (and the pop-out at /budget/compare) lets the
  // user compare against ANY version ad-hoc, but the Cost Report's
  // Variance section always uses this stable anchor.
  const pdfCompareDefaultTargetId = useMemo(() => {
    if (!report?.versions || report.versions.length < 2) return null;
    // Step 1+2: explicit baseline flag, checked across ALL versions so
    // we can detect the degenerate "current is also baseline" case.
    const explicit = report.versions.find(v => v.is_baseline);
    if (explicit) {
      if (explicit.is_current) return null;
      return explicit.id;
    }
    // Step 3: legacy fallback — oldest non-current by id.
    const prevs = report.versions.filter(v => !v.is_current);
    if (prevs.length === 0) return null;
    return prevs.reduce((oldest, v) => (v.id < oldest.id ? v : oldest), prevs[0]).id;
  }, [report]);
  const [pdfDefaultCompareData, setPdfDefaultCompareData] = useState(null);

  useEffect(() => {
    if (!departmentId || !pdfCompareDefaultTargetId) {
      setPdfDefaultCompareData(null);
      return;
    }
    const current = report?.versions?.find(v => v.is_current);
    if (!current || current.id === pdfCompareDefaultTargetId) {
      setPdfDefaultCompareData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet(`/api/budget/versions/compare?dept_id=${departmentId}&version_a=${current.id}&version_b=${pdfCompareDefaultTargetId}`);
        if (!res.ok) throw new Error('Failed to load variance comparison');
        const data = await res.json();
        if (!cancelled) setPdfDefaultCompareData(data);
      } catch {
        // F11 (silent-failure survey): nothing FALSE is stated when this
        // fails — the Variance Analysis option simply doesn't appear in the
        // Cost Report PDF menu, and the Cost Report itself still exports
        // fine. But a silently narrowed control on a MONEY export deserves
        // more than an absence nobody is told about, so warn rather than
        // leave the declared ignore silent. Not gated on `cancelled`: even a
        // stale effect's failure is true information the user should see.
        if (!cancelled) {
          toast.error('Failed to load the variance comparison — the Cost Report PDF will not offer a Variance Analysis section this time.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [departmentId, pdfCompareDefaultTargetId, report, toast]);

  const toggleSummaryView = () => {
    setSummaryView(prev => {
      const next = !prev;
      try { localStorage.setItem('sfx_budget_overview_summary', String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const toggleAllowancesExpanded = () => setAllowancesExpanded(prev => !prev);
  const toggleLaboursExpanded = () => setLaboursExpanded(prev => !prev);

  // Open the compare drawer for a non-current version. Fetches the diff
  // payload from /api/budget/versions/compare. The current version id is
  // resolved off `report.versions` (the report payload always carries
  // is_current=1 for exactly one row when versions exist).
  // Shared fetch — both the initial drawer-open and the silent cross-tab
  // refresh route through here. `silent` skips loading-state flicker so
  // the open drawer doesn't re-render the spinner mid-view when a
  // budget-changed event fires on another tab.
  const fetchCompareData = useCallback(async (currentVersionId, targetVersionId, { silent = false } = {}) => {
    if (!silent) {
      setCompareData(null);
      setCompareError(null);
      setCompareLoading(true);
    }
    try {
      const res = await apiGet(`/api/budget/versions/compare?dept_id=${departmentId}&version_a=${currentVersionId}&version_b=${targetVersionId}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load comparison');
      }
      const data = await res.json();
      setCompareData(data);
      if (silent) setCompareError(null);
    } catch (err) {
      // On silent refresh we KEEP the prior `compareData` rather than
      // wiping it — better to show slightly-stale numbers than to blank
      // the drawer because of a transient network blip. Only surface the
      // error on the initial open.
      if (!silent) setCompareError(friendlyErrorMessage(err));
    } finally {
      if (!silent) setCompareLoading(false);
    }
  }, [departmentId]);

  const openCompareDrawer = useCallback(async (targetVersionId) => {
    if (!departmentId || !report) return;
    const current = report.versions?.find(v => v.is_current);
    if (!current || current.id === targetVersionId) return;
    setCompareDrawerOpen(true);
    setCompareTargetVersionId(targetVersionId);
    setCompareUnchangedExpanded(false);
    setCompareCostCodesExpanded(true);
    setCompareLabourExpanded(true);
    await fetchCompareData(current.id, targetVersionId);
  }, [departmentId, report, fetchCompareData]);

  const closeCompareDrawer = () => {
    setCompareDrawerOpen(false);
    setCompareTargetVersionId(null);
    setCompareData(null);
    setCompareError(null);
  };

  // Cross-tab live update: when a budget-changed event fires on another
  // tab (or this tab regains focus) AND the drawer is open against a
  // valid target, silently re-fetch the comparison data. Use case: buyer
  // has the drawer on monitor A while editing crew/cost-codes on monitor
  // B — changes show up in the drawer within ~50ms via the dataBus event,
  // without the user touching the drawer.
  const silentRefetchCompare = useCallback(() => {
    if (!compareDrawerOpen || !compareTargetVersionId || !report) return;
    const current = report.versions?.find(v => v.is_current);
    if (!current || current.id === compareTargetVersionId) return;
    fetchCompareData(current.id, compareTargetVersionId, { silent: true });
  }, [compareDrawerOpen, compareTargetVersionId, report, fetchCompareData]);

  // ESC-to-close for the Compare drawer. Drawer is intentionally non-modal
  // (no backdrop, table/Gantt stay interactive) so we don't trap focus —
  // just listen for Escape while open. Hook placed ABOVE every early return
  // below to satisfy the Rules of Hooks (see feedback_react_hooks_above_returns).
  useEffect(() => {
    if (!compareDrawerOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeCompareDrawer();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [compareDrawerOpen]);

  // Fetch the budget report. `silent` skips the loading spinner so
  // background re-fetches (cross-tab invalidation, tab focus) don't
  // flash the page. The first load always shows the spinner.
  const fetchReport = useCallback(async ({ silent = false } = {}) => {
    if (!departmentId) return;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res = await apiGet(`/api/budget/report?department_id=${departmentId}&as_of=${asOfDate}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to fetch budget report');
      }
      const data = await res.json();
      setReport(data);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [departmentId, asOfDate]);

  useEffect(() => {
    if (departmentId) {
      fetchReport();
    }
  }, [departmentId, fetchReport]);

  // Silent cross-tab refresh: re-fetch when another tab edits an order
  // (cost-code / status / line items) in this department, or when the
  // user switches back to this tab. Zero server cost until something
  // actually changes.
  const silentRefetch = useCallback(() => fetchReport({ silent: true }), [fetchReport]);
  useBudgetInvalidation(departmentId, silentRefetch);

  // Compare drawer cross-tab live update — same dataBus + visibilitychange
  // wiring as the report above, scoped to fire only when the drawer is
  // open against a valid target.
  useBudgetInvalidation(departmentId, silentRefetchCompare);

  // Clamp future "As Of" dates back to today on blur.
  const handleAsOfBlur = () => {
    const today = todayISO();
    if (asOfDate > today) setAsOfDate(today);
  };

  // Redirect to Manage Budgets if no versions exist yet
  const hasNoVersions = report && report.versions && report.versions.length === 0;
  useEffect(() => {
    if (!loading && hasNoVersions) {
      navigate('/budget/manage-budgets', { replace: true });
    }
  }, [loading, hasNoVersions, navigate]);

  // Override the Layout page header with the active budget version
  // (e.g. "Budget: Aurora v1.0"). Layout keeps the production name as
  // the smaller-font subtitle.
  const currentVersionName = report?.versions?.find(v => v.is_current)?.version_name || '';
  usePageTitle(currentVersionName ? `Budget: ${currentVersionName}` : null);

  // Which OLDER versions show as columns (and print) — the `Compare: N of M ▾`
  // picker. Above the early returns (Rules of Hooks). Remembered per department
  // on this device; see components/budget/budgetCompareSelection.js.
  const compare = useBudgetCompareSelection(departmentId, report?.versions);

  // No department selected
  if (!departmentId && !isAdmin) {
    return (
      <div className="p-4 md:p-6">
        <p className="text-gray-600 dark:text-gray-400">Please select a department to view the budget overview.</p>
      </div>
    );
  }

  // Blocker #13: budget is OFF in personal workspaces. Show a friendly
  // empty-state instead of the usual pages (server would 403 on mutations).
  if (activeDepartment?.department_is_personal || activeDepartment?.production_is_personal) {
    return (
      <div className="p-6 md:p-10">
        <div className="max-w-lg mx-auto text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">Budgeting isn't available in My Space</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Join or create a production to use budgets, cost codes, and crew planning.
          </p>
        </div>
      </div>
    );
  }

  const departmentName = activeDepartment?.department_name || 'Department';
  const productionName = activeDepartment?.production_name || '';

  // Phase 10 / Task 17 — render the compare drawer markup. Kept as an inline
  // helper rather than a separate component so it shares the page's existing
  // formatMoney / dark-mode patterns with zero prop plumbing. The drawer is a
  // fixed-position panel (NOT a modal — we don't dim the page) anchored to
  // the top of the viewport so it floats above the budget table without
  // disturbing the Gantt/table layout below.
  const renderCompareDrawer = () => {
    if (!compareDrawerOpen) return null;
    // The drawer is a fixed-position panel (NOT a modal — we don't dim
    // the page) anchored to the top of the viewport so it floats above
    // the budget table without disturbing the Gantt/table layout below.
    // The body delegates to <CompareViewBody />, the same component the
    // pop-out page (/budget/compare) renders — single source of truth.
    const targetVersion = report?.versions?.find(v => v.id === compareTargetVersionId);
    const headingB = targetVersion?.version_name || 'Comparison';
    const currentVersion = report?.versions?.find(v => v.is_current);
    const popoutUrl = (currentVersion && compareTargetVersionId && departmentId)
      ? `/budget/compare?dept=${departmentId}&version_a=${currentVersion.id}&version_b=${compareTargetVersionId}`
      : null;

    return (
      <div
        className="fixed left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-6xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl overflow-hidden flex flex-col"
        style={{ top: '2rem', maxHeight: 'calc(100vh - 7rem)' }}
        role="dialog"
        aria-modal="false"
        aria-label="Version comparison"
      >
        {/* Top-right floating action group: pop-out + close. The pop-out
            uses <a target="_blank"> so middle-click + Ctrl+click work
            naturally and no pop-up blocker intervenes. */}
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1">
          {popoutUrl && (
            <a
              href={popoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Open in new tab — drag onto a second monitor for live multi-screen variance review"
              aria-label="Open comparison in a new tab"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 0 0 7M21 5l-9 9M21 14v5a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h5" />
              </svg>
            </a>
          )}
          <button
            type="button"
            onClick={closeCompareDrawer}
            className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Close"
            aria-label="Close comparison"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {compareLoading && (
            <div className="p-4">
              <LoadingSpinner message="Loading comparison..." />
            </div>
          )}
          {compareError && !compareLoading && (
            <div className="m-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300 px-4 py-3 rounded text-sm">
              {compareError}
            </div>
          )}
          {compareData && !compareLoading && (
            <CompareViewBody
              compareData={compareData}
              currentVersionName={currentVersionName || 'Current'}
              comparisonVersionName={headingB}
              productionName={productionName}
              departmentName={departmentName}
              companyName={activeDepartment?.company_name || ''}
              formatMoney={formatMoney}
              formatDateShort={formatDateShort}
              costCodesExpanded={compareCostCodesExpanded}
              onToggleCostCodes={() => setCompareCostCodesExpanded(prev => !prev)}
              labourExpanded={compareLabourExpanded}
              onToggleLabour={() => setCompareLabourExpanded(prev => !prev)}
              unchangedExpanded={compareUnchangedExpanded}
              onToggleUnchanged={() => setCompareUnchangedExpanded(prev => !prev)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`p-4 pt-0 lg:p-6 lg:pt-0 space-y-6 ${PAGE_SIDE_GUTTER}`}>
      {renderCompareDrawer()}
      {/* ONE ROW: the Cost Reports | Edit Budgets tabs, and — from md — As of ·
          Compare · Actions · ? at its right end (owner, 2026-10-05: the header
          card "can be made less tall"). Mirrors Edit Budgets' version controls
          on the same row (`components/budget/BudgetTabsNav.js`). The card's
          "Cost Report <version>" h2 went with it: the tab and the app bar name it.
          Below md the row is a column, so the controls sit on a line beneath. */}
      <div className={BUDGET_TABS_BAND}>
        <div className={BUDGET_TABS_ROW}>
          <BudgetTabsNav />
          {!loading && report && !hasNoVersions && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 pb-2 md:pb-1.5 md:ml-auto shrink-0" data-testid="cost-report-controls">
              <label className="text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5">
                <span className="hidden sm:inline">As of:</span>
                <input
                  type="date"
                  value={asOfDate}
                  max={todayISO()}
                  onChange={(e) => setAsOfDate(e.target.value)}
                  onBlur={handleAsOfBlur}
                  className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <BudgetComparePicker
                older={compare.older}
                selectedIds={compare.selectedIds}
                max={compare.max}
                atCap={compare.atCap}
                onToggle={compare.toggle}
                onLatestOnly={compare.latestOnly}
                onShowAll={compare.showAll}
              />
              <SharePdfButton
                extras={(() => {
                  const list = [
                    { key: 'overview', label: 'Budget Overview', defaultActive: true, filenameSuffix: '' },
                  ];
                  // PDF variance ALWAYS uses the silently-prefetched baseline
                  // (oldest non-current version). Drawer choice no longer
                  // overrides — see pdfCompareDefaultTargetId.
                  const targetName = report?.versions?.find(v => v.id === pdfCompareDefaultTargetId)?.version_name || 'Baseline';
                  if (pdfDefaultCompareData && pdfCompareDefaultTargetId) {
                    list.push({
                      key: 'variance',
                      label: `Variance Analysis (vs ${targetName})`,
                      defaultActive: true,
                      filenameSuffix: '-with-variance',
                    });
                    list.push({
                      key: 'unchanged',
                      label: 'unchanged crew & cost codes',
                      defaultActive: false,
                      filenameSuffix: '-with-unchanged',
                    });
                  }
                  return list;
                })()}
                filename={`${departmentName}-Cost-Report.pdf`}
                title={`${departmentName} Cost Report`}
                orientation="landscape"
                dropDirection="down"
                className={TOOLBAR_DISCLOSURE_TRIGGER}
                renderPdfMakeDoc={({ activeExtras }) => {
                  // pdfmake path — vector text PDF for any combination
                  // of Cost Report + Variance Analysis (Phase 2). The
                  // composer in budgetVariancePdf.js handles page-breaks
                  // between sections; html2pdf path stays as a fallback
                  // for callers that don't opt in.
                  const includeOverview = !!activeExtras?.overview;
                  const includeVariance = !!activeExtras?.variance && !!pdfDefaultCompareData;
                  if (!includeOverview && !includeVariance) return null;
                  const baselineName = report?.versions?.find(v => v.id === pdfCompareDefaultTargetId)?.version_name || 'Baseline';
                  // No centred title strip here: the Cost Report's own
                  // header (now "<Dept> Cost Report — <Production>" + the
                  // "Cost to date vs current budget (<v>)" line) already
                  // carries this identity, so a separate strip duplicated it.
                  return buildBudgetVariancePdf({
                    includeOverview,
                    includeVariance,
                    includeUnchanged: !!activeExtras?.unchanged,
                    reportData: report,
                    departmentName,
                    productionName,
                    companyName: activeDepartment?.company_name || '',
                    /* 🔻 `includeVat` NO LONGER PASSED (2026-08-08). It only
                       ever drove the variance PDF's subtitle suffix
                       ("· incl. VAT" / "· ex. VAT"), and with the VAT display
                       removed the app cannot honestly assert either. Omitting
                       it is the whole fix: `utils/variancePdf.js` already
                       guards that suffix with `includeVat !== undefined`, so
                       an absent value drops the claim rather than printing the
                       wrong half of it. Do not pass `false` "to be explicit" —
                       that prints "· ex. VAT". */
                    formatMoney,
                    // What you see is what you print: the Cost Report
                    // section shows exactly the ticked older versions.
                    // The Variance section ignores it (its own baseline).
                    compareVersionIds: compare.selectedIds,
                    versionName: report?.versions?.find(v => v.is_current)?.version_name || '',
                    baselineName,
                    compareData: pdfDefaultCompareData,
                    currentVersionName: currentVersionName || 'Current',
                    comparisonVersionName: baselineName,
                    baselineVersionId: pdfCompareDefaultTargetId,
                    // baselineLabel: omitted → defaults to "Baseline"
                    // (accurate — Overview anchor IS the actual oldest).
                  });
                }}
              >
                Actions
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </SharePdfButton>
              {/* Help affordance — clarifies what's in the file so the
                  buyer doesn't confuse it with the Compare pop-out's
                  ad-hoc Version Comparison Report. */}
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-600 cursor-help"
                title="Status Report — current cost breakdown plus variance vs the baseline. To compare two specific budget versions, open the Compare drawer and use its pop-out icon."
                aria-label="About the Status Report download"
              >
                ?
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 font-bold"
          >
            &times;
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && <LoadingSpinner message="Loading budget report..." />}

      {/* No versions → redirect handled by useEffect above */}

      {/* Main Report Content */}
      {!loading && report && !hasNoVersions && (
        <>

          {/* The hidden BudgetPrintLayout for window.print() was removed in
              the pdfmake migration. Ctrl+P now prints the on-screen UI as
              the browser would on any normal page; the formatted Cost Report
              + Variance Analysis PDF is available via the Actions ▾ menu
              above (it was "Download / Print ▾" until 2026-10-05),
              generating selectable vector text via pdfmake. */}

          {/* Cost Report Table */}
          {(() => {
            // Only the TICKED older versions (Compare picker), in the table's
            // newest-first order. Every column site below maps this one list.
            const prevVersions = compare.older.filter(v => compare.selectedIds.includes(Number(v.id)));
            // Cell-level height is the only reliable enforcement across browsers
            // for sibling tables — TR-level height is treated as a hint and
            // ignored when content is shorter. Pin TD height via descendant
            // selector + box-border so padding is included in the height.
            const ROW_H = 'h-[37px] [&>td]:box-border [&>td]:h-[37px]';
            const ROW_H_HEADER = 'h-[33px] [&>td]:box-border [&>td]:h-[33px]';
            const ROW_H_GRAND = 'h-[41px] [&>td]:box-border [&>td]:h-[41px]';
            const ROW_STYLE = { height: 37, minHeight: 37 };
            const ROW_STYLE_HEADER = { height: 33, minHeight: 33 };
            const ROW_STYLE_GRAND = { height: 41, minHeight: 41 };

            // Pre-compute cost code row data. ccDisplayPercents clamps the
            // negative-net-spend case (manual adjustment below zero) so
            // "% Left" cannot read over 100 — see its docblock.
            const ccRows = report.cost_codes.map(cc => {
              const { percentSpend, percentLeft } = ccDisplayPercents(cc.budget_amount, cc.cost_to_date);
              const health = getBudgetHealth(percentSpend);
              const healthColors = BUDGET_HEALTH_COLORS[health];
              return { ...cc, percentSpend, percentLeft, health, healthColors };
            });

            // Resolve which prior version to show inline budget deltas against.
            // Drawer-open with a target wins; otherwise the silently-pre-fetched
            // PDF default target. The bracket shows (±£X) next to Current Budget
            // when this code's budget differs from the reference version.
            const drawerTargetExists = !!report?.versions?.find(v => v.id === compareTargetVersionId);
            const usingDrawer = !!compareData && drawerTargetExists;
            const inlineRefVersionId = usingDrawer ? compareTargetVersionId : pdfCompareDefaultTargetId;
            const inlineRefVersion = report?.versions?.find(v => v.id === inlineRefVersionId) || null;
            const inlineRefVersionName = inlineRefVersion?.version_name || null;
            // Generic red/green "(+£X)" / "(−£X)" pill used by both Cost
            // Codes AND Labour rows so they share styling + tone semantics.
            // Returns null when delta is 0 OR ref is missing (e.g. the row
            // doesn't exist in the baseline version — added later).
            const renderInlineDelta = (current, ref, refName) => {
              if (ref == null) return null;
              const delta = (Number(current) || 0) - (Number(ref) || 0);
              if (!delta) return null;
              const tone = delta > 0
                ? 'text-red-600 dark:text-red-400'
                : 'text-emerald-600 dark:text-emerald-400';
              const sign = delta > 0 ? '+' : '−';
              return (
                <span className={`ml-1 text-xs font-semibold ${tone}`} title={refName ? `vs ${refName}` : undefined}>
                  ({sign}{formatMoney(Math.abs(delta))})
                </span>
              );
            };
            const renderInlineCcDelta = (cc) => {
              if (!inlineRefVersionId) return null;
              return renderInlineDelta(cc.budget_amount, cc.version_budgets?.[inlineRefVersionId], inlineRefVersionName);
            };

            return (
              <>
              {/* Mobile card layout — one card per cost code, section subtotals, grand total */}
              <div className="md:hidden space-y-3">
                {/* Purchases & Rentals section */}
                {!summaryView && ccRows.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 px-1">Purchases &amp; Rentals</h4>
                    {ccRows.map(cc => (
                      <div key={cc.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{cc.code}</span>
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{cc.name}</p>
                          </div>
                          <span className={`flex-shrink-0 inline-flex px-2 py-1 rounded text-xs font-semibold ${cc.healthColors.bg} ${cc.healthColors.text}`}>
                            {cc.percentSpend.toFixed(1)}%
                          </span>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">Spent</p>
                            <p className="font-semibold text-gray-800 dark:text-gray-200">
                              {formatMoney(cc.cost_to_date)}
                              {/* cost_to_date INCLUDES the manual spend adjustment
                                  (server /report); the marker says so. Computed
                                  half = cost_to_date - spend_adjustment. */}
                              <SpendAdjustmentMarker
                                computed={(Number(cc.cost_to_date) || 0) - (Number(cc.spend_adjustment) || 0)}
                                adjustment={cc.spend_adjustment}
                              />
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">Budget</p>
                            <p className="font-semibold text-gray-800 dark:text-gray-200">
                              {formatMoney(cc.budget_amount)}
                              {renderInlineCcDelta(cc)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">Unspent</p>
                            <p className={`font-semibold ${Number(cc.unspent) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'}`}>{formatMoney(cc.unspent)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">% Left</p>
                            <p className="font-semibold text-gray-800 dark:text-gray-200">{cc.percentLeft.toFixed(1)}%</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Purchases Subtotal card */}
                    <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                      <p className="text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 mb-1">Purchases Subtotal</p>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{formatMoney(report.grand_total?.purchases_actual || 0)}</span>
                          <span className="text-gray-500 dark:text-gray-400 mx-1">of</span>
                          {formatMoney(report.grand_total?.purchases_budget || 0)}
                        </span>
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {report.grand_total?.purchases_budget > 0 ? ((report.grand_total.purchases_actual / report.grand_total.purchases_budget) * 100).toFixed(1) + '%' : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Labour section */}
                {!summaryView && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 px-1">Labour</h4>
                  {/* Crew Rates (mobile) — tap header to expand into per-member cards */}
                  {(() => {
                    const totLab = report.crew_summary?.total_labour || 0;
                    const breakdown = report.crew_summary?.labour_breakdown || [];
                    return (
                      <>
                        <button
                          type="button"
                          onClick={toggleLaboursExpanded}
                          aria-expanded={laboursExpanded}
                          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm flex items-baseline justify-between"
                        >
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-800 dark:text-gray-200">
                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${laboursExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            Crew Rates
                          </span>
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                            {formatMoney(totLab)}
                            {renderInlineDelta(totLab, inlineRefVersion?.crew_labour, inlineRefVersionName)}
                          </span>
                        </button>
                        {laboursExpanded && breakdown.map((item) => {
                          const start = formatDateShort(item.start_date);
                          const end = formatDateShort(item.end_date);
                          const wks = item.total_weeks != null ? `${item.total_weeks} wk${item.total_weeks === 1 ? '' : 's'}` : null;
                          const metaParts = [];
                          if (start !== '-' && end !== '-') metaParts.push(`${start} → ${end}`);
                          if (wks) metaParts.push(wks);
                          return (
                            <div key={`mobile-labour-${item.id}`} className="bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 p-3 ml-4 shadow-sm flex items-baseline justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm text-gray-700 dark:text-gray-300 truncate">{item.name}</div>
                                {metaParts.length > 0 && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{metaParts.join(' · ')}</div>
                                )}
                              </div>
                              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                {formatMoney(item.budgeted)}
                                {item.lineage_id && renderInlineDelta(
                                  item.budgeted,
                                  inlineRefVersion?.crew_labour_by_lineage?.[item.lineage_id],
                                  inlineRefVersionName
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                  {/* Total Allowances (mobile) — tap header to expand into per-name child cards */}
                  {(() => {
                    const totAllow = report.crew_summary?.total_allowances ?? report.crew_summary?.total_computer_rental ?? 0;
                    const breakdown = report.crew_summary?.allowance_breakdown || [];
                    return (
                      <>
                        <button
                          type="button"
                          onClick={toggleAllowancesExpanded}
                          aria-expanded={allowancesExpanded}
                          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm flex items-baseline justify-between"
                        >
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-800 dark:text-gray-200">
                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${allowancesExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            Total Allowances
                          </span>
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                            {formatMoney(totAllow)}
                            {renderInlineDelta(
                              totAllow,
                              inlineRefVersion ? (Number(inlineRefVersion.crew_computer) || 0) + (Number(inlineRefVersion.crew_allowance) || 0) : null,
                              inlineRefVersionName
                            )}
                          </span>
                        </button>
                        {allowancesExpanded && breakdown.map((item, idx) => (
                          <div key={`mobile-allowance-${idx}`} className="bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 p-3 ml-4 shadow-sm flex items-baseline justify-between">
                            <span className="text-sm text-gray-700 dark:text-gray-300">{item.name}</span>
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                              {formatMoney(item.budgeted)}
                              {renderInlineDelta(
                                item.budgeted,
                                inlineRefVersion?.crew_allowance_by_name?.[String(item.name || '').toLowerCase()],
                                inlineRefVersionName
                              )}
                            </span>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                  {/* Labour Subtotal card */}
                  <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                    <p className="text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 mb-1">Labour Subtotal</p>
                    <div className="flex items-baseline justify-end">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatMoney(report.grand_total?.labour_budget || 0)}</span>
                    </div>
                  </div>
                </div>
                )}

                {/* Grand Total card */}
                <div className="bg-slate-100 dark:bg-slate-700/30 rounded-lg p-3 border-2 border-slate-300 dark:border-slate-600">
                  <p className="text-xs font-bold uppercase text-gray-600 dark:text-gray-400 mb-1">
                    Grand Total
                  </p>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-base font-bold text-gray-900 dark:text-white">
                      {formatMoney(report.grand_total?.total_actual || 0)}
                      <span className="text-gray-500 dark:text-gray-400 font-normal text-sm mx-1">of</span>
                      <span className="text-gray-700 dark:text-gray-300 font-semibold text-sm">{formatMoney(report.grand_total?.total_budget || 0)}</span>
                    </span>
                    <span className={`text-sm font-bold whitespace-nowrap ${(report.grand_total?.total_budget || 0) - (report.grand_total?.total_actual || 0) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      {report.grand_total?.total_budget > 0 ? ((report.grand_total.total_actual / report.grand_total.total_budget) * 100).toFixed(1) + '%' : '-'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Unspent: <span className={`font-semibold ${(report.grand_total?.total_budget || 0) - (report.grand_total?.total_actual || 0) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'}`}>{formatMoney((report.grand_total?.total_budget || 0) - (report.grand_total?.total_actual || 0))}</span>
                  </p>
                </div>
              </div>

              {/* Desktop table — single unified table with main columns + appended
                  prev-version columns. Row heights are inherently identical because
                  every row IS one row. A border-l-2 on the first prev-version cell
                  visually separates the main columns from the version cluster.
                  The card is overflow-CLIP, never overflow-hidden: a hidden
                  card is a scroll container and the sticky scrollbar would pin
                  to it, i.e. stay under the Grand Total row, off-screen
                  (components/budget/BudgetTableScroller.js). */}
              <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-clip" data-testid="budget-overview-table-card">
                <BudgetTableScroller testId="budget-overview-table-scroller" dragPan>
                  <table className="text-sm w-full">
                    <thead>
                      <tr
                        className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/40 transition-colors"
                        data-section-title
                        aria-expanded={!summaryView}
                        onClick={toggleSummaryView}
                        title={summaryView ? 'Click to show detail rows' : 'Click to hide detail rows'}
                      >
                        <th className="text-left px-3 py-3 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap text-sm">Cost Code</th>
                        <th className="text-left px-3 py-3 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap text-sm">Description</th>
                        <th className="text-right px-3 py-3 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap text-sm">Current Budget</th>
                        <th className="text-right px-3 py-3 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap text-sm">Cost to Date</th>
                        <th className="text-right px-3 py-3 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap text-sm">Unspent</th>
                        <th className="text-right px-3 py-3 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap text-sm">% Spend</th>
                        <th className="text-right px-3 py-3 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap text-sm">
                          <span className="inline-flex items-center justify-end gap-1.5 w-full">
                            % Left
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${summaryView ? '' : 'rotate-180'}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </span>
                        </th>
                        {prevVersions.length > 0 && prevVersions.map((v, i) => (
                          <th
                            key={v.id}
                            className={`text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap text-sm min-w-[100px] ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-2">
                              {/* A long version name is truncated so the compare columns rarely
                                  force a horizontal scroll (owner 2026-10-06); the full name is
                                  in the tooltip. */}
                              <span
                                className={`min-w-0 max-w-[8rem] truncate pb-1 ${v.is_baseline ? 'underline decoration-2 underline-offset-4 font-semibold text-gray-700 dark:text-gray-200' : ''}`}
                                title={v.is_baseline ? `${v.version_name} — variance baseline (anchor for Variance Analysis)` : v.version_name}
                                data-testid={`compare-col-name-${v.id}`}
                              >
                                {v.version_name}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openCompareDrawer(v.id); }}
                                className="text-xs font-medium px-2 py-0.5 rounded border border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                                title={`Compare ${currentVersionName || 'current'} → ${v.version_name}`}
                              >
                                Compare
                              </button>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {!summaryView && (
                      <>
                      {/* Purchases Header */}
                      <tr className={`bg-gray-100 dark:bg-gray-700/30 ${ROW_H_HEADER}`} style={ROW_STYLE_HEADER}>
                        <td colSpan={7} className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Purchases &amp; Rentals</td>
                        {prevVersions.length > 0 && prevVersions.map((v, i) => (
                          <td key={v.id} className={`px-3 py-2 ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}></td>
                        ))}
                      </tr>
                      {/* Cost Code Rows */}
                      {ccRows.map(cc => (
                        <tr key={cc.id} className={`border-b border-gray-100 dark:border-gray-700/50 ${ROW_H}`} style={ROW_STYLE}>
                          <td className="px-3 py-2 font-mono text-gray-800 dark:text-gray-200 whitespace-nowrap">{cc.code}</td>
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{cc.name}</td>
                          <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">
                            {formatMoney(cc.budget_amount)}
                            {renderInlineCcDelta(cc)}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">
                            {formatMoney(cc.cost_to_date)}
                            <SpendAdjustmentMarker
                              computed={(Number(cc.cost_to_date) || 0) - (Number(cc.spend_adjustment) || 0)}
                              adjustment={cc.spend_adjustment}
                            />
                          </td>
                          <td className={`px-3 py-2 text-right whitespace-nowrap ${Number(cc.unspent) < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-800 dark:text-gray-200'}`}>{formatMoney(cc.unspent)}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${cc.healthColors.bg} ${cc.healthColors.text}`}>{cc.percentSpend.toFixed(1)}%</span>
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{cc.percentLeft.toFixed(1)}%</td>
                          {prevVersions.length > 0 && prevVersions.map((v, i) => (
                            <td
                              key={v.id}
                              className={`px-3 py-2 text-right text-gray-500 dark:text-gray-500 whitespace-nowrap text-sm ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}
                            >
                              {cc.version_budgets?.[v.id] != null ? formatMoney(cc.version_budgets[v.id]) : '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {/* Purchases Subtotal */}
                      <tr className={`border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 font-semibold ${ROW_H}`} style={ROW_STYLE}>
                        <td colSpan={2} className="px-3 py-2 text-gray-700 dark:text-gray-300">Purchases Subtotal</td>
                        <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatMoney(report.grand_total?.purchases_budget || 0)}</td>
                        <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatMoney(report.grand_total?.purchases_actual || 0)}</td>
                        <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatMoney((report.grand_total?.purchases_budget || 0) - (report.grand_total?.purchases_actual || 0))}</td>
                        <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{report.grand_total?.purchases_budget > 0 ? ((report.grand_total.purchases_actual / report.grand_total.purchases_budget) * 100).toFixed(1) + '%' : '-'}</td>
                        <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{report.grand_total?.purchases_budget > 0 ? (Math.max(0, 100 - (report.grand_total.purchases_actual / report.grand_total.purchases_budget) * 100)).toFixed(1) + '%' : '-'}</td>
                        {prevVersions.length > 0 && prevVersions.map((v, i) => (
                          <td
                            key={v.id}
                            className={`px-3 py-2 text-right text-gray-500 dark:text-gray-500 whitespace-nowrap text-sm font-semibold ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}
                          >
                            {formatMoney(v.total_budget || 0)}
                          </td>
                        ))}
                      </tr>
                      {/* Labour Header */}
                      <tr className={`bg-gray-100 dark:bg-gray-700/30 ${ROW_H_HEADER}`} style={ROW_STYLE_HEADER}>
                        <td colSpan={7} className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Labour</td>
                        {prevVersions.length > 0 && prevVersions.map((v, i) => (
                          <td key={v.id} className={`px-3 py-2 ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}></td>
                        ))}
                      </tr>
                      {/* Crew Rates — expandable: per-crew-member breakdown */}
                      {(() => {
                        const totLab = report.crew_summary?.total_labour || 0;
                        const totLabActual = report.crew_summary?.total_labour_actual || 0;
                        const breakdown = report.crew_summary?.labour_breakdown || [];
                        const pctSpend = totLab > 0 ? ((totLabActual / totLab) * 100).toFixed(1) + '%' : '-';
                        const pctLeft = totLab > 0 ? (Math.max(0, 100 - (totLabActual / totLab) * 100)).toFixed(1) + '%' : '-';
                        return (
                          <>
                            <tr
                              className={`border-b border-gray-100 dark:border-gray-700/50 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/40 transition-colors ${ROW_H}`}
                              onClick={toggleLaboursExpanded}
                              aria-expanded={laboursExpanded}
                              title={laboursExpanded ? 'Click to collapse crew breakdown' : 'Click to expand crew breakdown'}
                            >
                              <td colSpan={2} className="px-3 py-2 text-gray-800 dark:text-gray-200">
                                <span className="inline-flex items-center gap-1.5">
                                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${laboursExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  Crew Rates
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">
                                {formatMoney(totLab)}
                                {renderInlineDelta(totLab, inlineRefVersion?.crew_labour, inlineRefVersionName)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatMoney(totLabActual)}</td>
                              <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatMoney(totLab - totLabActual)}</td>
                              <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{pctSpend}</td>
                              <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{pctLeft}</td>
                              {prevVersions.length > 0 && prevVersions.map((v, i) => (
                                <td
                                  key={v.id}
                                  className={`px-3 py-2 text-right text-gray-500 dark:text-gray-500 whitespace-nowrap text-sm ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}
                                >
                                  {v.crew_labour != null ? formatMoney(v.crew_labour) : '-'}
                                </td>
                              ))}
                            </tr>
                            {laboursExpanded && breakdown.map((item) => {
                              const childPctSpend = item.budgeted > 0 ? ((item.actual / item.budgeted) * 100).toFixed(1) + '%' : '-';
                              const childPctLeft = item.budgeted > 0 ? (Math.max(0, 100 - (item.actual / item.budgeted) * 100)).toFixed(1) + '%' : '-';
                              const start = formatDateShort(item.start_date);
                              const end = formatDateShort(item.end_date);
                              const wks = item.total_weeks != null ? `${item.total_weeks} wk${item.total_weeks === 1 ? '' : 's'}` : null;
                              const metaParts = [];
                              if (start !== '-' && end !== '-') metaParts.push(`${start} → ${end}`);
                              if (wks) metaParts.push(wks);
                              return (
                                <tr key={`labour-${item.id}`} className={`border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/60 dark:bg-slate-700/20 ${ROW_H}`} style={ROW_STYLE}>
                                  <td colSpan={2} className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                    <div className="pl-6">
                                      <div>{item.name}</div>
                                      {metaParts.length > 0 && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{metaParts.join(' · ')}</div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    {formatMoney(item.budgeted)}
                                    {item.lineage_id && renderInlineDelta(
                                      item.budgeted,
                                      inlineRefVersion?.crew_labour_by_lineage?.[item.lineage_id],
                                      inlineRefVersionName
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatMoney(item.actual)}</td>
                                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatMoney(item.budgeted - item.actual)}</td>
                                  <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{childPctSpend}</td>
                                  <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{childPctLeft}</td>
                                  {prevVersions.length > 0 && prevVersions.map((v, i) => {
                                    // Look up this crew member's labour cost in
                                    // each prev version via the per-lineage
                                    // map on the report payload. Missing → "—"
                                    // (the member doesn't exist in that
                                    // version — added later / deleted earlier).
                                    const val = item.lineage_id
                                      ? v.crew_labour_by_lineage?.[item.lineage_id]
                                      : undefined;
                                    return (
                                      <td
                                        key={v.id}
                                        className={`px-3 py-2 text-right text-gray-500 dark:text-gray-500 whitespace-nowrap text-sm ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}
                                      >
                                        {val != null ? formatMoney(val) : '—'}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </>
                        );
                      })()}
                      {/* Total Allowances — expandable: Computer + named allowance slots */}
                      {(() => {
                        const totAllow = report.crew_summary?.total_allowances ?? report.crew_summary?.total_computer_rental ?? 0;
                        const totAllowActual = report.crew_summary?.total_allowances_actual ?? report.crew_summary?.total_computer_rental_actual ?? 0;
                        const breakdown = report.crew_summary?.allowance_breakdown || [];
                        const pctSpend = totAllow > 0 ? ((totAllowActual / totAllow) * 100).toFixed(1) + '%' : '-';
                        const pctLeft = totAllow > 0 ? (Math.max(0, 100 - (totAllowActual / totAllow) * 100)).toFixed(1) + '%' : '-';
                        return (
                          <>
                            <tr
                              className={`border-b border-gray-100 dark:border-gray-700/50 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/40 transition-colors ${ROW_H}`}
                              onClick={toggleAllowancesExpanded}
                              aria-expanded={allowancesExpanded}
                              title={allowancesExpanded ? 'Click to collapse allowance breakdown' : 'Click to expand allowance breakdown'}
                            >
                              <td colSpan={2} className="px-3 py-2 text-gray-800 dark:text-gray-200">
                                <span className="inline-flex items-center gap-1.5">
                                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${allowancesExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  Total Allowances
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">
                                {formatMoney(totAllow)}
                                {renderInlineDelta(
                                  totAllow,
                                  inlineRefVersion ? (Number(inlineRefVersion.crew_computer) || 0) + (Number(inlineRefVersion.crew_allowance) || 0) : null,
                                  inlineRefVersionName
                                )}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatMoney(totAllowActual)}</td>
                              <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatMoney(totAllow - totAllowActual)}</td>
                              <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{pctSpend}</td>
                              <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{pctLeft}</td>
                              {prevVersions.length > 0 && prevVersions.map((v, i) => {
                                // Total Allowances = computer rental + named-allowance subtotal.
                                // Both are version-scoped aggregates on the report payload.
                                const compute = (Number(v.crew_computer) || 0) + (Number(v.crew_allowance) || 0);
                                const hasAny = v.crew_computer != null || v.crew_allowance != null;
                                return (
                                  <td
                                    key={v.id}
                                    className={`px-3 py-2 text-right text-gray-500 dark:text-gray-500 whitespace-nowrap text-sm ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}
                                  >
                                    {hasAny ? formatMoney(compute) : '-'}
                                  </td>
                                );
                              })}
                            </tr>
                            {allowancesExpanded && breakdown.map((item, idx) => {
                              const childPctSpend = item.budgeted > 0 ? ((item.actual / item.budgeted) * 100).toFixed(1) + '%' : '-';
                              const childPctLeft = item.budgeted > 0 ? (Math.max(0, 100 - (item.actual / item.budgeted) * 100)).toFixed(1) + '%' : '-';
                              return (
                                <tr key={`allowance-${idx}`} className={`border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/60 dark:bg-slate-700/20 ${ROW_H}`} style={ROW_STYLE}>
                                  <td colSpan={2} className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                    <span className="pl-6 inline-block">{item.name}</span>
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    {formatMoney(item.budgeted)}
                                    {renderInlineDelta(
                                      item.budgeted,
                                      inlineRefVersion?.crew_allowance_by_name?.[String(item.name || '').toLowerCase()],
                                      inlineRefVersionName
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatMoney(item.actual)}</td>
                                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatMoney(item.budgeted - item.actual)}</td>
                                  <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{childPctSpend}</td>
                                  <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{childPctLeft}</td>
                                  {prevVersions.length > 0 && prevVersions.map((v, i) => {
                                    // Look up this allowance's value in each
                                    // prev version by case-folded name. Missing
                                    // → "—" (this allowance didn't exist in
                                    // that version's crew config).
                                    const key = String(item.name || '').toLowerCase();
                                    const val = v.crew_allowance_by_name?.[key];
                                    return (
                                      <td
                                        key={v.id}
                                        className={`px-3 py-2 text-right text-gray-500 dark:text-gray-500 whitespace-nowrap text-sm ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}
                                      >
                                        {val != null ? formatMoney(val) : '—'}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </>
                        );
                      })()}
                      {/* 🔀 JOB COST CODES (2026-10-28): the ad-hoc lines that sat here
                          are job codes now — they list with the cost codes above, and
                          the labour section is crew only (server `/report`). */}
                      {/* Labour Subtotal */}
                      <tr className={`border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 font-semibold ${ROW_H}`} style={ROW_STYLE}>
                        <td colSpan={2} className="px-3 py-2 text-gray-700 dark:text-gray-300">Labour Subtotal</td>
                        <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatMoney(report.grand_total?.labour_budget || 0)}</td>
                        <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatMoney(report.grand_total?.labour_actual || 0)}</td>
                        <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatMoney((report.grand_total?.labour_budget || 0) - (report.grand_total?.labour_actual || 0))}</td>
                        <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{report.grand_total?.labour_budget > 0 ? ((report.grand_total.labour_actual / report.grand_total.labour_budget) * 100).toFixed(1) + '%' : '-'}</td>
                        <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400 whitespace-nowrap">{report.grand_total?.labour_budget > 0 ? (Math.max(0, 100 - (report.grand_total.labour_actual / report.grand_total.labour_budget) * 100)).toFixed(1) + '%' : '-'}</td>
                        {prevVersions.length > 0 && prevVersions.map((v, i) => (
                          <td
                            key={v.id}
                            className={`px-3 py-2 text-right text-gray-500 dark:text-gray-500 whitespace-nowrap text-sm font-semibold ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}
                          >
                            {v.crew_total != null ? formatMoney(v.crew_total) : '-'}
                          </td>
                        ))}
                      </tr>
                      </>
                      )}
                      {/* Grand Total */}
                      <tr className={`border-t-2 border-gray-400 dark:border-gray-500 bg-slate-100 dark:bg-slate-700/30 font-bold ${ROW_H_GRAND}`} style={ROW_STYLE_GRAND}>
                        <td colSpan={2} className="px-3 py-3 text-gray-900 dark:text-gray-100">
                          Grand Total
                        </td>
                        <td className="px-3 py-3 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatMoney(report.grand_total?.total_budget || 0)}</td>
                        <td className="px-3 py-3 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatMoney(report.grand_total?.total_actual || 0)}</td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <span className={`${(report.grand_total?.total_budget || 0) - (report.grand_total?.total_actual || 0) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>{formatMoney((report.grand_total?.total_budget || 0) - (report.grand_total?.total_actual || 0))}</span>
                        </td>
                        <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{report.grand_total?.total_budget > 0 ? ((report.grand_total.total_actual / report.grand_total.total_budget) * 100).toFixed(1) + '%' : '-'}</td>
                        <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{report.grand_total?.total_budget > 0 ? (Math.max(0, 100 - (report.grand_total.total_actual / report.grand_total.total_budget) * 100)).toFixed(1) + '%' : '-'}</td>
                        {prevVersions.length > 0 && prevVersions.map((v, i) => (
                          <td
                            key={v.id}
                            className={`px-3 py-3 text-right text-gray-500 dark:text-gray-500 whitespace-nowrap text-sm font-medium ${i === 0 ? 'border-l-2 border-gray-300 dark:border-gray-600' : ''}`}
                          >
                            {formatMoney((v.total_budget || 0) + (v.crew_total || 0))}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </BudgetTableScroller>
              </div>
              </>
            );
          })()}

          {/* Spend vs Budget Bar Charts */}
          {report.cost_codes.length > 0 && (
            <CollapsibleSection id="budget-spend-vs-budget" title="Spend vs Budget by Cost Code" defaultCollapsed>
              <div className="p-4 space-y-4">
              <div className="space-y-3">
                {report.cost_codes.map(cc => {
                  const budget = Number(cc.budget_amount) || 0;
                  const spend = Number(cc.cost_to_date) || 0;
                  const maxVal = Math.max(budget, spend, 1);
                  const budgetWidth = (budget / maxVal) * 100;
                  // Negative net spend renders a zero-width bar, not a negative one.
                  const spendWidth = (Math.max(0, spend) / maxVal) * 100;
                  const { percentSpend } = ccDisplayPercents(cc.budget_amount, cc.cost_to_date);
                  const health = getBudgetHealth(percentSpend);
                  const spendBarColor = health === 'good'
                    ? 'bg-indigo-500 dark:bg-indigo-400'
                    : health === 'warning'
                      ? 'bg-amber-500 dark:bg-amber-400'
                      : 'bg-red-500 dark:bg-red-400';

                  return (
                    <div key={cc.id} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-gray-700 dark:text-gray-300 truncate mr-2">
                          {cc.code} — {cc.name}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {formatMoney(spend)} / {formatMoney(budget)}
                        </span>
                      </div>
                      <div className="relative h-5 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                        {/* Budget bar (background) */}
                        <div
                          className="absolute inset-y-0 left-0 bg-gray-200 dark:bg-gray-600 rounded"
                          style={{ width: `${budgetWidth}%` }}
                        />
                        {/* Spend bar (overlay) */}
                        <div
                          className={`absolute inset-y-0 left-0 ${spendBarColor} rounded transition-all duration-300`}
                          style={{ width: `${spendWidth}%` }}
                        />
                        {/* Percentage label */}
                        {budget > 0 && (
                          <span className="absolute inset-y-0 right-2 flex items-center text-[10px] font-semibold text-gray-600 dark:text-gray-300">
                            {percentSpend.toFixed(0)}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded bg-gray-200 dark:bg-gray-600" /> Budget
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded bg-indigo-500 dark:bg-indigo-400" /> Spend
                </span>
              </div>
              </div>
            </CollapsibleSection>
          )}
        </>
      )}
    </div>
  );
}

export default BudgetOverview;
